import { CountryCodeType, COUNTRY_CODE, GEOGRAPHIC_REGION_PREFIX } from 'constants/country';
import {
  baseCountryFilterTreeNode,
  baseGeographicRegionTreeNode,
  baseOtherCountryTreeNode,
  COUNTRY_FILTER_TREE_NODE_ID,
  CountryType,
  StateType
} from 'constants/filter';
import { TransformedFilterTreeNode } from 'components/tree/FilterTree';

//
// --- Constants ---

/** list of codes to be display on their own, not included in other countries list */
const primaryCountryCodes: ReadonlyArray<CountryCodeType> = [COUNTRY_CODE.UNITED_STATES, COUNTRY_CODE.CANADA];

//
// --- Country Tree Data Transform Functions ---

export function transformCountryListToTreeNodes(
  countries: ReadonlyArray<CountryType>
): ReadonlyArray<TransformedFilterTreeNode> {
  // generate tree nodes for countries that are displayed outside auto complete (primary)
  const primaryCountryTreeNodes: ReadonlyArray<TransformedFilterTreeNode> = primaryCountryCodes
    .map(countryCode => countries.find(country => country.label === countryCode))
    .reduce(
      (primaryTreeNodes, currentCountry) =>
        currentCountry
          ? [...primaryTreeNodes, transformCountryToTreeNode(currentCountry, COUNTRY_FILTER_TREE_NODE_ID.ROOT)]
          : primaryTreeNodes,
      [] as ReadonlyArray<TransformedFilterTreeNode>
    );

  // generate list of tree nodes for other countries
  const otherCountryTreeNodes = countries
    .filter(country => !primaryCountryCodes.includes(country.label))
    .reduce(
      (otherTreeNodes, currentCountry) =>
        currentCountry
          ? [...otherTreeNodes, transformCountryToTreeNode(currentCountry, COUNTRY_FILTER_TREE_NODE_ID.OTHER)]
          : otherTreeNodes,
      [] as ReadonlyArray<TransformedFilterTreeNode>
    );

  return [
    {
      ...baseCountryFilterTreeNode,
      children: [
        ...primaryCountryTreeNodes,
        { ...baseOtherCountryTreeNode, children: Array.isArray(otherCountryTreeNodes) ? otherCountryTreeNodes : [] }
      ]
    }
  ];
}

function transformCountryToTreeNode(country: CountryType, parentId: string): TransformedFilterTreeNode {
  const treeNodeId = `${GEOGRAPHIC_REGION_PREFIX.COUNTRY}${country.id}`;
  const hasChildren = Array.isArray(country.children) && country.children?.length > 0;

  return {
    ...baseGeographicRegionTreeNode,
    children: hasChildren
      ? country.children!.map((state: StateType) => {
          return transformStateToTreeNode(state, treeNodeId);
        })
      : [],
    filterChipLabel: country.label,
    id: treeNodeId,
    leaf: !hasChildren,
    name: country.name,
    parentId,
    tooltip: country.name
  };
}

function transformStateToTreeNode(state: StateType, parentId: string): TransformedFilterTreeNode {
  const treeNodeId = `${GEOGRAPHIC_REGION_PREFIX.STATE}${state.id}`;

  return {
    ...baseGeographicRegionTreeNode,
    children: [],
    filterChipLabel: state.label,
    id: treeNodeId,
    leaf: true,
    name: state.name,
    parentId,
    tooltip: state.name
  };
}

function getIdsByPrefixFromCountryStateList(countryOrStateIdList: ReadonlyArray<string>, prefix: string) {
  if (!countryOrStateIdList || !Array.isArray(countryOrStateIdList)) {
    return [];
  }

  return countryOrStateIdList
    .filter(countryStateId => countryStateId.startsWith(prefix))
    .map(countryStateId => {
      const [, id] = countryStateId.split('_');
      return id;
    });
}

export function getStateIdsFromCountryStateList(countryOrStateIdList: ReadonlyArray<string>) {
  return getIdsByPrefixFromCountryStateList(countryOrStateIdList, 'S_');
}

export function getCountryIdsFromCountryStateList(countryOrStateIdList: ReadonlyArray<string>) {
  return getIdsByPrefixFromCountryStateList(countryOrStateIdList, 'C_');
}
